import { useState } from 'react';
import { Table, Button, Modal, message, Tag } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useGetWithdrawals, useApproveWithdrawal } from 'api/affiliator';

const WithdrawalList = () => {
    const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
    const [approvingId, setApprovingId] = useState<number | null>(null);

    const { data: withdrawals, isFetching, error, refetch } = useGetWithdrawals();
    const approveMutation = useApproveWithdrawal();

    const confirmApprove = (id: number) => {
        setApprovingId(id);
        setIsApproveModalVisible(true);
    };

    const handleApprove = () => {
        if (approvingId) {
            approveMutation.mutate(approvingId, {
                onSuccess: () => {
                    refetch();
                    setIsApproveModalVisible(false);
                    setApprovingId(null);
                    message.success('Penarikan dana berhasil disetujui');
                },
                onError: (error) => {
                    console.error("Failed to approve:", error);
                    message.error('Gagal menyetujui penarikan dana');
                },
            });
        }
    };

    const columns = [
        {
            title: 'No',
            key: 'index',
            render: (_: any, __: any, index: number) => index + 1,
        },
        {
            title: 'Tanggal Request',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date: string) => new Date(date).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })
        },
        {
            title: 'Nama Affiliator',
            key: 'affiliator_name',
            render: (_: any, record: any) => record.affiliator?.name || '-'
        },
        {
            title: 'Bank / E-Money',
            dataIndex: 'bank_name',
            key: 'bank_name',
            render: (bank_name: string) => bank_name || '-'
        },
        {
            title: 'Detail Rekening',
            key: 'account_detail',
            render: (_: any, record: any) => (
                <div>
                    <div className="font-semibold">{record.account_number || '-'}</div>
                    <div className="text-xs text-gray-500">a.n. {record.account_name || '-'}</div>
                </div>
            )
        },
        {
            title: 'Nominal Penarikan',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount: number) => `Rp ${amount.toLocaleString('id-ID')}`
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                if (status === 'PENDING') return <Tag color="warning">Menunggu Proses</Tag>;
                if (status === 'PAID') return <Tag color="success">Sudah Dibayar</Tag>;
                return <Tag>{status}</Tag>;
            }
        },
        {
            title: 'Aksi',
            key: 'action',
            render: (_: any, record: any) => (
                <div className="flex gap-2">
                    {record.status === 'PENDING' && (
                        <Button
                            onClick={() => confirmApprove(record.id)}
                            type="primary"
                            size="small"
                            icon={<CheckOutlined />}
                        >
                            Setujui Transfer
                        </Button>
                    )}
                </div>
            ),
        },
    ];

    if (error) return <p>Error loading withdrawals</p>;

    return (
        <div className='bg-white p-4 rounded-lg'>
            <div className='flex justify-between items-center mb-6'>
                <h1 style={{ fontSize: 24, fontWeight: 'bold' }}>Riwayat Penarikan Dana</h1>
            </div>

            <Table
                loading={isFetching}
                columns={columns}
                dataSource={withdrawals?.data?.data || []}
                rowKey="id"
                bordered
            />

            <Modal
                title="Konfirmasi Persetujuan Transfer"
                visible={isApproveModalVisible}
                onOk={handleApprove}
                onCancel={() => setIsApproveModalVisible(false)}
                okText="Ya, Sudah Ditransfer"
                cancelText="Batal"
            >
                <p>Apakah Anda yakin ingin menyetujui penarikan dana ini?</p>
                <p className="text-gray-500 text-sm">Pastikan Anda sudah melakukan transfer ke nomor/rekening affiliator sebelum menekan tombol setuju.</p>
            </Modal>
        </div>
    );
};

export default WithdrawalList;
